import React, { useState } from "react"
import {
	FaCalendarAlt,
	FaCheckCircle,
	FaInfoCircle,
	FaEye,
	FaEyeSlash,
	FaLink
} from "react-icons/fa"

// Issue type icons
const issueTypeIcons = {
	linked: <FaLink className="text-gray-500 mr-2 flex-shrink-0" />,
	schedule: <FaCalendarAlt className="text-gray-500 mr-2 flex-shrink-0" />
}

// Helper function to get the appropriate icon based on the issue message content
function getIssueIcon(category) {
	if (category.includes("Linked")) {
		return issueTypeIcons.linked
	} else {
		return issueTypeIcons.schedule
	}
}

function groupIssuesByCategory(issues) {
	const map = {}
	issues?.forEach(issue => {
		const cat = issue.type || "Other"
		if (!map[cat]) map[cat] = []
		map[cat].push(issue)
	})
	return Object.entries(map).map(([category, issues]) => ({ category, issues }))
}

function flattenGroupedIssues(grouped) {
	const arr = []
	grouped.forEach(cat => {
		cat.issues.forEach(issue => arr.push({ ...issue, _category: cat.category }))
	})
	return arr
}

const SchedulingIssues = ({ issues }) => {
	const [expanded, setExpanded] = useState(false)
	const grouped = groupIssuesByCategory(issues || [])
	const total = issues?.length || 0

	// Flatten for slicing, then regroup for display
	const flat = flattenGroupedIssues(grouped)
	const toShow = expanded ? flat : flat.slice(0, 7)

	// Regroup the toShow issues for display
	const displayGrouped = groupIssuesByCategory(toShow)

	if (!total) {
		return (
			<div className="bg-white rounded-xl shadow p-6 h-full flex flex-col items-center justify-center">
				<FaCheckCircle className="text-4xl text-green-500 mb-4" />
				<h3 className="text-xl font-bold text-gray-800 mb-2">All Clear!</h3>
				<p className="text-gray-600 text-center">
					No scheduling conflicts or issues to address at this time.
				</p>
			</div>
		)
	}

	return (
		<div className="bg-white rounded-xl shadow p-6 h-full flex flex-col">
			<div className="flex justify-between items-center mb-4">
				<div className="flex items-center">
					<FaInfoCircle className="text-blue-600 mr-2" />
					<h3 className="text-xl font-bold">Scheduling Alerts</h3>
				</div>
				<span className="bg-yellow-100 text-yellow-800 rounded-full px-3 py-1 text-sm font-semibold">
					{total} {total === 1 ? "Alert" : "Alerts"}
				</span>
			</div>

			<div className="flex-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
				<div className="space-y-4">
					{displayGrouped.map(cat => (
						<div key={cat.category} className="pb-2">
							<div className="flex items-center mb-2 sticky top-0 bg-white z-10 py-1">
								<div className="text-sm text-gray-900 flex items-start">
									{getIssueIcon(cat.category)}
								</div>
								<span className="font-semibold text-lg">{cat.category}</span>
							</div>
							<ul className="ml-8 space-y-3">
								{cat.issues.map((issue, idx) => (
									<li key={idx} className="border-l-2 border-yellow-200 pl-3">
										<div className="text-sm text-gray-900">{issue.message}</div>
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</div>

			{total > 7 && (
				<div className=" pt-2 border-t border-gray-100">
					<button
						className="flex items-center justify-center mx-auto px-4 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-full text-sm transition-colors"
						onClick={() => setExpanded(e => !e)}
					>
						{expanded ? (
							<>
								<FaEyeSlash className="mr-2" />
								Show Fewer
							</>
						) : (
							<>
								<FaEye className="mr-2" />
								View All {total} Alerts
							</>
						)}
					</button>
				</div>
			)}
		</div>
	)
}

export default SchedulingIssues
